import styles from './PlayerPositionsCard.module.scss';

/** Dernière position connue d'un joueur sur un serveur faction (GET /api/players/{uuid}/positions). */
export interface PlayerPositionDTO {
  server: string;
  world: string | null;
  x: number | null;
  y: number | null;
  z: number | null;
  /** quit (déconnexion), transfer (changement de serveur) ou periodic (relevé toutes les 60 s). */
  reason: string | null;
  /** Millisecondes UTC. */
  updatedAt: number | null;
}

interface PlayerPositionsCardProps {
  positions: PlayerPositionDTO[];
  loading: boolean;
  /** Nom affiché du serveur (ex. faction-1 -> Faction 1), celui de la page joueur. */
  serverDisplayName?: (server: string) => string;
}

const REASONS: Record<string, string> = {
  quit: 'Déconnexion',
  transfer: 'Changement de serveur',
  periodic: 'En jeu',
};

const formatCoord = (value: number | null) => (value === null || value === undefined ? '?' : Math.floor(value).toString());

const formatDate = (millis: number | null) =>
  millis ? new Date(millis).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '?';

const defaultServerName = (server: string) => {
  const match = server.match(/faction-(\d+)/);
  return match ? `Faction ${match[1]}` : server;
};

/**
 * Tableau des dernières positions connues du joueur, une ligne par serveur faction (écrites par FactionsMod dans
 * factionapi.PlayerPositions à la déconnexion, au changement de serveur et toutes les 60 s).
 */
export function PlayerPositionsCard({ positions, loading, serverDisplayName = defaultServerName }: PlayerPositionsCardProps) {
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>Dernières positions</h3>
      {loading ? (
        <p className={styles.emptyText}>Chargement...</p>
      ) : positions.length === 0 ? (
        <p className={styles.emptyText}>Aucune position connue</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Serveur</th>
              <th>Monde</th>
              <th>X / Y / Z</th>
              <th>Relevé</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr key={position.server}>
                <td>{serverDisplayName(position.server)}</td>
                <td>{position.world ?? '?'}</td>
                <td className={styles.coords}>
                  {formatCoord(position.x)} / {formatCoord(position.y)} / {formatCoord(position.z)}
                </td>
                <td>{(position.reason && REASONS[position.reason]) ?? position.reason ?? '?'}</td>
                <td>{formatDate(position.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
